import React, { useState, useEffect, useRef } from "react";
import { X, Truck, User, Phone, Clock, CheckCircle, XCircle } from "lucide-react";
import hospitalService from "../../services/hospitalService";
import { authService } from "../../services";
import { useAuth } from "../../contexts/AuthContext";
import { useAllocationWebSocket } from "../../hooks/useAllocationWebSocket";
import { DEFAULT_MANUAL_ALLOCATION_TIMEOUT_SEC, getTimeoutConfig } from "../../services/configService";

const ManualAllocationModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  bookingData,
  selectedAmbulance,
  ambulances = [], // Available ambulance types
  allocationStatus = null, // Allocation status from poll API
  bookingId = null, // Current booking ID
  isPollingAllocation = false, // Whether polling is active
  onRefetchCandidates = null, // Callback to refetch candidates (for timeout scenario)
  onStopAllocation = null, // Callback to stop allocation process when cancel is clicked
  onRefetchAllocationStatus = null, // Callback to refetch allocation status (for WebSocket integration)
}) => {
  const [allocationTimeoutSec, setAllocationTimeoutSec] = useState(DEFAULT_MANUAL_ALLOCATION_TIMEOUT_SEC);
  const [countdown, setCountdown] = useState(DEFAULT_MANUAL_ALLOCATION_TIMEOUT_SEC);
  const [driverResponse, setDriverResponse] = useState(null); // 'accepted', 'rejected', 'timeout'
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [assignedDriver, setAssignedDriver] = useState(null);
  const [currentDriverIndex, setCurrentDriverIndex] = useState(0);
  const [showDriverSelection, setShowDriverSelection] = useState(false); // New state for manual driver selection
  const [showAmbulanceSelection, setShowAmbulanceSelection] = useState(false); // New state for ambulance type selection
  const [rejectedDriverIndex, setRejectedDriverIndex] = useState(null); // Track which driver was rejected
  const [currentSelectedAmbulance, setCurrentSelectedAmbulance] = useState(selectedAmbulance); // Track currently selected ambulance type
  const [isConfirmingBooking, setIsConfirmingBooking] = useState(false); // Track if booking confirmation is in progress
  const simulationTimeoutRef = useRef(null);
  const previousResponseIdRef = useRef(null); // Track previous response_id to detect new allocations
  const hasTriggeredSuccessRef = useRef(false); // Track if success modal has been triggered for current allocation
  const timerIntervalRef = useRef(null); // Track the countdown interval
  const timerStartTimeRef = useRef(null); // Store when timer started (timestamp-based)
  const lastDisplayedCountdownRef = useRef(null); // Track last displayed countdown value to prevent unnecessary updates
  const isConfirmingBookingRef = useRef(false); // Track if booking confirmation is in progress to prevent duplicates
  const expiresAtRef = useRef(null); // Store absolute expiration time from API
  
  // Get user context to determine if this is a fleet user
  const { user } = useAuth();

  // Load backend timeout configuration (45s default)
  useEffect(() => {
    getTimeoutConfig().then((cfg) => {
      if (cfg?.manual_allocation_timeout_sec) {
        setAllocationTimeoutSec(cfg.manual_allocation_timeout_sec);
      }
    });
  }, []);

  // WebSocket integration: Listen for allocation status changes and trigger refetch
  useAllocationWebSocket(
    bookingId,
    () => {
      if (onRefetchAllocationStatus && typeof onRefetchAllocationStatus === 'function') {
        onRefetchAllocationStatus();
      }
    },
    isOpen && !!bookingId && !!onRefetchAllocationStatus
  );
  
  // Helper: Check if user is a fleet user
  const isFleetUser = () => {
    return user?.role === 'TENANT_ADMIN' ||
      user?.role === 'TENANT_USER' ||
      user?.role === 'tenant_admin' ||
      user?.role === 'Fleet';
  };

  // Pool of different drivers with varying details
  const driverPool = [
    {
      name: 'Rajesh Kumar',
      phone: '+91 98765 43210',
      rating: '4.8',
      distance: '2.5 km away',
      vehicleNumber: 'AMB-001',
      ambulanceVariant: 'Advanced Life Support',
      estimatedFare: '650'
    },
    {
      name: 'Priya Sharma',
      phone: '+91 98765 43211',
      rating: '4.6',
      distance: '3.2 km away',
      vehicleNumber: 'AMB-002',
      ambulanceVariant: 'Advanced Life Support Plus',
      estimatedFare: '750'
    },
    {
      name: 'Amit Patel',
      phone: '+91 98765 43212',
      rating: '4.9',
      distance: '1.8 km away',
      vehicleNumber: 'AMB-003',
      ambulanceVariant: 'Critical Care Transport',
      estimatedFare: '850'
    },
    {
      name: 'Sunita Singh',
      phone: '+91 98765 43213',
      rating: '4.7',
      distance: '4.1 km away',
      vehicleNumber: 'AMB-004',
      ambulanceVariant: 'Advanced Life Support',
      estimatedFare: '600'
    },
    {
      name: 'Vikram Singh',
      phone: '+91 98765 43214',
      rating: '4.5',
      distance: '2.9 km away',
      vehicleNumber: 'AMB-005',
      ambulanceVariant: 'Emergency Response Unit',
      estimatedFare: '700'
    },
    {
      name: 'Anita Gupta',
      phone: '+91 98765 43215',
      rating: '4.8',
      distance: '3.5 km away',
      vehicleNumber: 'AMB-006',
      ambulanceVariant: 'Mobile ICU',
      estimatedFare: '900'
    }
  ];

  // Get current driver based on index
  const getCurrentDriver = () => {
    return driverPool[currentDriverIndex % driverPool.length];
  };

  // Current driver data
  const currentDriver = getCurrentDriver();

  // Handle allocation status changes from API
  // IMPORTANT: Timer is UI-driven only - countdown is NOT updated from API expires_in_sec
  // The 45-second timer starts independently when a new allocation begins and counts down using client-side timestamp
  useEffect(() => {
    if (allocationStatus) {
      // Reset countdown when a new allocation starts (new response_id)
      const currentResponseId = allocationStatus.response_id;
      if (currentResponseId && currentResponseId !== previousResponseIdRef.current) {
        // New allocation detected - reset countdown and driver response
        previousResponseIdRef.current = currentResponseId;
        hasTriggeredSuccessRef.current = false; // Reset success trigger flag
        
        // CRITICAL: Store expires_at for timer synchronization (per integration doc)
        if (allocationStatus.driver_response?.expires_at) {
          expiresAtRef.current = new Date(allocationStatus.driver_response.expires_at).getTime();
          console.log('⏱️ [ManualAllocationModal] Stored expires_at for timer sync:', allocationStatus.driver_response.expires_at);
        } else {
          // Fallback: Use 45 seconds (or config API) if expires_at not available
          expiresAtRef.current = null;
        }
        
        setCountdown(allocationTimeoutSec); // Initial value, will be updated by timer effect if expires_at is available
        setDriverResponse(null);
        setShowConfirmation(false);
      }
      
      // Update expires_at if it changes (e.g., after refetch)
      if (allocationStatus.driver_response?.expires_at) {
        const newExpiresAt = new Date(allocationStatus.driver_response.expires_at).getTime();
        if (expiresAtRef.current !== newExpiresAt) {
          expiresAtRef.current = newExpiresAt;
          console.log('⏱️ [ManualAllocationModal] Updated expires_at:', allocationStatus.driver_response.expires_at);
        }
      }
      
      // Update driver response based on API status
      // IMPORTANT: Only show confirmation when driver is ACTUALLY assigned via real-time API
      // Require both: driver_response status === 'ACCEPTED' AND actual driver data from API
      if (allocationStatus.driver_response?.status === 'ACCEPTED' && 
          !hasTriggeredSuccessRef.current &&
          allocationStatus.driver) { // CRITICAL: Only proceed if driver data exists from API
        
        // Validate that we have real driver data (not dummy/simulated)
        const hasRealDriverData = allocationStatus.driver?.name || 
                                  allocationStatus.driver?.driver_name ||
                                  allocationStatus.driver?.phone ||
                                  allocationStatus.driver?.phone_number;
        
        if (!hasRealDriverData) {
          console.warn('⚠️ [MANUAL ALLOCATION] Driver accepted but no real driver data from API - waiting for real-time data');
          return; // Don't show confirmation without real driver data
        }
        
        setDriverResponse('accepted');
        hasTriggeredSuccessRef.current = true; // Mark that success has been triggered
        
        // Set assigned driver from allocationStatus (real-time data from API)
        const assignedDriverData = {
          driver: {
            name: allocationStatus.driver?.name || 
                  allocationStatus.driver?.driver_name ||
                  'Driver Name',
            phone: allocationStatus.driver?.phone || 
                   allocationStatus.driver?.phone_number ||
                   'N/A',
            rating: allocationStatus.driver?.rating || 'N/A'
          },
          ambulance: {
            name: allocationStatus.vehicle?.type ||
                  selectedAmbulance?.name || 
                  'Ambulance',
            vehicleNumber: allocationStatus.vehicle?.number ||
                          allocationStatus.vehicle?.vehicle_number ||
                          selectedAmbulance?.plate_number || 
                          'N/A',
            price: allocationStatus.estimated_fare ||
                   bookingData?.estimatedFare ||
                   selectedAmbulance?.estimated_fare || 
                   'N/A',
          }
        };
        
        console.log('✅ [MANUAL ALLOCATION] Driver accepted with REAL-TIME API data - setting assignedDriver:', assignedDriverData);
        setAssignedDriver(assignedDriverData);
        
        // Show confirmation UI ONLY with real-time data from API
        // Small delay to ensure state is set before showing confirmation
        setTimeout(() => {
          console.log('✅ [MANUAL ALLOCATION] Showing confirmation UI with REAL-TIME API data');
          setShowConfirmation(true);
        }, 500);
      } else if (allocationStatus.driver_response?.status === 'DECLINED') {
        setDriverResponse('rejected');
      } else if (allocationStatus.ui_state === 'TIMEOUT_PICK_ANOTHER') {
        setDriverResponse('timeout');
      }
    }
  }, [allocationStatus, selectedAmbulance, bookingData]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('🚑 [MODAL] Manual allocation modal opened');
      
      // Clear any existing timers first
      if (simulationTimeoutRef.current) {
        clearTimeout(simulationTimeoutRef.current);
        simulationTimeoutRef.current = null;
      }
      if (timerIntervalRef.current) {
        console.log('🧹 [MODAL] Clearing existing timer interval on modal open');
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      
      // Reset all state and refs
      setCountdown(allocationTimeoutSec);
      setDriverResponse(null);
      setShowConfirmation(false);
      setAssignedDriver(null);
      setCurrentDriverIndex(0); // Start with first driver
      setShowDriverSelection(false); // Reset driver selection view
      setShowAmbulanceSelection(false); // Reset ambulance selection view
      setRejectedDriverIndex(null); // Reset rejected driver index
      setCurrentSelectedAmbulance(selectedAmbulance); // Reset to initial selected ambulance
      setIsConfirmingBooking(false); // Reset booking confirmation state
      previousResponseIdRef.current = null; // Reset previous response ID tracker
      hasTriggeredSuccessRef.current = false; // Reset success trigger flag
      isConfirmingBookingRef.current = false; // Reset booking confirmation lock
      
      console.log('🚑 [MODAL] State reset complete, timer will start via useEffect');
      console.log('🔓 [MODAL] Booking confirmation lock released');
      
      // NOTE: Driver response is handled ONLY via real-time API polling
      // No simulation/dummy flows - confirmation modal only appears after real driver assignment
      // The allocationStatus prop will be updated via polling when driver responds
      // Timer will automatically start via the timer useEffect
    }
  }, [isOpen, selectedAmbulance]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (simulationTimeoutRef.current) {
        clearTimeout(simulationTimeoutRef.current);
        simulationTimeoutRef.current = null;
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  // Countdown timer - using expires_at from API (per integration doc requirement)
  // Falls back to 45-second UI timer if expires_at is not available
  useEffect(() => {
    // Clear any existing timer first
    if (timerIntervalRef.current) {
      console.log('🧹 [TIMER] Clearing existing timer interval');
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
      timerStartTimeRef.current = null;
      lastDisplayedCountdownRef.current = null;
    }

    // Start new timer if conditions are met
    if (isOpen && !driverResponse && !showConfirmation) {
      const updateTimer = () => {
        let remaining = 0;
        
        if (expiresAtRef.current) {
          // PRIORITY: Use expires_at (absolute time) for accurate countdown
          const now = Date.now();
          remaining = Math.max(0, Math.floor((expiresAtRef.current - now) / 1000));
        } else if (timerStartTimeRef.current) {
          // FALLBACK: Use 45-second UI timer with timestamp-based countdown
          const TIMER_DURATION = allocationTimeoutSec;
          const elapsed = Math.floor((Date.now() - timerStartTimeRef.current) / 1000);
          remaining = Math.max(0, TIMER_DURATION - elapsed);
        } else {
          return;
        }
        
        // Only update state when the second value actually changes
        if (remaining !== lastDisplayedCountdownRef.current) {
          lastDisplayedCountdownRef.current = remaining;
          setCountdown(remaining);
        }
        
        if (remaining <= 0) {
          console.log('⏰ [TIMER] Timer expired - calling handleDriverTimeout');
          // Clear the interval
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          timerStartTimeRef.current = null;
          expiresAtRef.current = null;
          // Timeout - driver did not respond
          handleDriverTimeout();
        }
      };

      // Initialize timer
      if (expiresAtRef.current) {
        // Use expires_at - no need to set start time
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((expiresAtRef.current - now) / 1000));
        lastDisplayedCountdownRef.current = remaining;
        setCountdown(remaining);
        console.log('⏱️ [TIMER] Starting timer from expires_at, remaining:', remaining);
      } else {
        // Fallback: Use 45-second UI timer
        const TIMER_DURATION = allocationTimeoutSec;
        timerStartTimeRef.current = Date.now();
        lastDisplayedCountdownRef.current = TIMER_DURATION;
        setCountdown(TIMER_DURATION);
        console.log('⏱️ [TIMER] Starting fallback timer from:', TIMER_DURATION);
      }
      
      // Update immediately
      updateTimer();
      
      // Use 100ms interval for smooth updates
      timerIntervalRef.current = setInterval(updateTimer, 100);
    } else {
      console.log('⏹️ [TIMER] Timer not started. Conditions:', {
        isOpen,
        driverResponse,
        showConfirmation
      });
    }

    return () => {
      if (timerIntervalRef.current) {
        console.log('🧹 [TIMER CLEANUP] Clearing timer interval');
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
        timerStartTimeRef.current = null;
        lastDisplayedCountdownRef.current = null;
      }
    };
  }, [isOpen, driverResponse, showConfirmation, allocationStatus?.driver_response?.expires_at]); // Re-run when expires_at changes

  // DEPRECATED: This function should NOT be called directly
  // Driver acceptance is handled ONLY via real-time API polling (allocationStatus prop)
  // This function is kept for backward compatibility but should not trigger confirmation
  const handleDriverAccept = () => {
    console.warn('⚠️ [MANUAL ALLOCATION] handleDriverAccept called - this should only be triggered by real-time API data');
    console.warn('⚠️ [MANUAL ALLOCATION] Confirmation modal will NOT be shown from this function');
    
    // Stop any simulation timers
    if (simulationTimeoutRef.current) {
      clearTimeout(simulationTimeoutRef.current);
      simulationTimeoutRef.current = null;
    }
    
    // DO NOT set driver response or show confirmation here
    // Confirmation is ONLY shown when allocationStatus.driver_response?.status === 'ACCEPTED'
    // AND allocationStatus.driver exists (real-time API data)
  };

  const handleDriverReject = () => {
    console.log('❌ Driver rejected the request');
    setDriverResponse('rejected');
    setRejectedDriverIndex(currentDriverIndex); // Track which driver was rejected
  };

  const handleDriverTimeout = () => {
    console.log('⏰ Driver timed out');
    setDriverResponse('timeout');
  };

  const handleChooseAnotherDriver = () => {
    // Move to next driver in the pool (skip rejected driver if any)
    setCurrentDriverIndex(prev => {
      const nextIndex = (prev + 1) % driverPool.length;
      // Skip the rejected driver if it's the next one
      if (rejectedDriverIndex !== null && nextIndex === rejectedDriverIndex) {
        return (nextIndex + 1) % driverPool.length;
      }
      return nextIndex;
    });
    
    // Reset to initial state to "choose another driver"
    setCountdown(allocationTimeoutSec);
    setDriverResponse(null);
    setShowConfirmation(false);
    setAssignedDriver(null);
    setShowDriverSelection(false);
    
    // Clear any existing timeout
    if (simulationTimeoutRef.current) {
      clearTimeout(simulationTimeoutRef.current);
      simulationTimeoutRef.current = null;
    }
    
    // NOTE: Driver response is handled ONLY via real-time API polling
    // No simulation/dummy flows - confirmation modal only appears after real driver assignment
    
    const nextIndex = rejectedDriverIndex !== null && (currentDriverIndex + 1) % driverPool.length === rejectedDriverIndex
      ? (currentDriverIndex + 2) % driverPool.length
      : (currentDriverIndex + 1) % driverPool.length;
    const newDriver = driverPool[nextIndex];
    console.log(`🔄 Choosing another driver: ${newDriver.name}, timer restarted`);
  };

  const handleShowDriverSelection = () => {
    setShowDriverSelection(true);
    setShowAmbulanceSelection(false);
  };

  const handleShowAmbulanceSelection = () => {
    setShowAmbulanceSelection(true);
    setShowDriverSelection(false);
  };

  const handleSelectAmbulanceType = (ambulanceType) => {
    console.log('🚑 [MANUAL ALLOCATION] Ambulance type selected:', ambulanceType);
    setCurrentSelectedAmbulance(ambulanceType);
    setShowAmbulanceSelection(false);
    setShowDriverSelection(false);
    
    // Reset driver selection state
    setCurrentDriverIndex(0);
    setCountdown(allocationTimeoutSec);
    setDriverResponse(null);
    setShowConfirmation(false);
    setAssignedDriver(null);
    setRejectedDriverIndex(null);
    
    // Clear any existing timeout
    if (simulationTimeoutRef.current) {
      clearTimeout(simulationTimeoutRef.current);
      simulationTimeoutRef.current = null;
    }
    
    // NOTE: Driver response is handled ONLY via real-time API polling
    // No simulation/dummy flows - confirmation modal only appears after real driver assignment
    console.log('🚑 [MANUAL ALLOCATION] Ambulance type selected, waiting for real-time driver assignment via API');
  };

  const handleSelectDriverManually = (driverIndex) => {
    console.log(`👤 Manually selecting driver at index: ${driverIndex}`);
    
    // Set the selected driver index
    setCurrentDriverIndex(driverIndex);
    
    // Reset to initial state
    setCountdown(allocationTimeoutSec);
    setDriverResponse(null);
    setShowConfirmation(false);
    setAssignedDriver(null);
    setShowDriverSelection(false);
    setRejectedDriverIndex(null);
    
    // Clear any existing timeout
    if (simulationTimeoutRef.current) {
      clearTimeout(simulationTimeoutRef.current);
      simulationTimeoutRef.current = null;
    }
    
    // NOTE: Driver response is handled ONLY via real-time API polling
    // No simulation/dummy flows - confirmation modal only appears after real driver assignment
    
    const selectedDriver = driverPool[driverIndex];
    console.log(`✅ Manually selected driver: ${selectedDriver.name}, timer restarted`);
  };

  const handleConfirmBooking = () => {
    // CRITICAL: Prevent duplicate booking confirmations
    if (isConfirmingBookingRef.current || isConfirmingBooking) {
      console.warn('⚠️ [MANUAL ALLOCATION] Booking confirmation already in progress - ignoring duplicate call');
      return;
    }
    
    // CRITICAL: Only allow confirmation if driver data exists from real-time API
    // Do NOT use dummy/simulated data
    if (!allocationStatus?.driver) {
      console.error('❌ [MANUAL ALLOCATION] Cannot confirm booking - no real-time driver data from API');
      return;
    }
    
    // Mark that confirmation is in progress (both ref and state)
    isConfirmingBookingRef.current = true;
    setIsConfirmingBooking(true);
    console.log('🔒 [MANUAL ALLOCATION] Booking confirmation locked - preventing duplicates');
    
    try {
      // Use real-time data from allocationStatus (REQUIRED - no fallback to dummy data)
      const driverData = {
        name: allocationStatus.driver?.name || 
              allocationStatus.driver?.driver_name ||
              'Driver Name',
        phone: allocationStatus.driver?.phone || 
               allocationStatus.driver?.phone_number ||
               'N/A',
        rating: allocationStatus.driver?.rating || 'N/A',
        driver_id: allocationStatus.driver?.driver_id || 
                  allocationStatus.driver?.id,
        id: allocationStatus.driver?.driver_id || 
            allocationStatus.driver?.id
      };

      const ambulanceData = allocationStatus?.vehicle ? {
        name: allocationStatus.vehicle?.type ||
              'Ambulance',
        vehicleNumber: allocationStatus.vehicle?.number ||
                       allocationStatus.vehicle?.vehicle_number ||
                       'N/A',
        vehicle_number: allocationStatus.vehicle?.number ||
                       allocationStatus.vehicle?.vehicle_number ||
                       'N/A',
        price: allocationStatus.estimated_fare ||
               bookingData?.estimatedFare ||
               'N/A',
        vehicle_id: allocationStatus.vehicle?.vehicle_id || 
                   allocationStatus.vehicle?.id,
        id: allocationStatus.vehicle?.vehicle_id || 
            allocationStatus.vehicle?.id
      } : null;
      
      // CRITICAL: Require both driver and vehicle data from API
      if (!ambulanceData) {
        console.error('❌ [MANUAL ALLOCATION] Cannot confirm booking - no real-time vehicle data from API');
        isConfirmingBookingRef.current = false;
        setIsConfirmingBooking(false);
        return;
      }

      if (driverData && ambulanceData) {
        console.log('🎉 [MANUAL ALLOCATION] Manual allocation confirmed with real-time data');
        console.log('🎉 [MANUAL ALLOCATION] Driver:', driverData);
        console.log('🎉 [MANUAL ALLOCATION] Ambulance:', ambulanceData);
        
        // Call the success callback with real-time data
        onSelect({
          ambulance: ambulanceData,
          driver: driverData,
        });
        
        // Close the modal
        onClose();
        
        // Note: Don't reset isConfirmingBookingRef/isConfirmingBooking here - let it stay locked
        // It will be reset when modal opens again
      } else {
        console.error('❌ [MANUAL ALLOCATION] Missing driver or ambulance data for confirmation');
        isConfirmingBookingRef.current = false;
        setIsConfirmingBooking(false);
      }
    } catch (error) {
      console.error('❌ [MANUAL ALLOCATION] Error in handleConfirmBooking:', error);
      isConfirmingBookingRef.current = false;
      setIsConfirmingBooking(false);
    }
  };

  const handleCancel = async () => {
    console.log('❌ [Manual Allocation] Cancel clicked - stopping allocation process');
    
    // Clear any existing timers
    if (simulationTimeoutRef.current) {
      clearTimeout(simulationTimeoutRef.current);
      simulationTimeoutRef.current = null;
    }
    if (timerIntervalRef.current) {
      console.log('🧹 [CANCEL] Clearing timer interval');
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Call stop-allocation API endpoint
    // Use fleet API for fleet users, hospital API for hospital users
    if (bookingId) {
      try {
        const isFleet = isFleetUser();
        console.log('🛑 [Manual Allocation] Calling stop-allocation API for bookingId:', bookingId, 'isFleetUser:', isFleet);
        
        let response;
        if (isFleet) {
          console.log('🚛 [Manual Allocation] Using fleet stop-allocation API');
          response = await authService.fleetStopAllocation(bookingId);
        } else {
          console.log('🏥 [Manual Allocation] Using hospital stop-allocation API');
          response = await hospitalService.stopAllocation(bookingId);
        }
        
        console.log('✅ [Manual Allocation] Stop-allocation API response:', response);
        
        // Handle real-time data from API response
        // Fleet API response structure: { success: true, message: "...", data: { success: true, message: "...", data: { allocation_stopped: true, driver_request_cancelled: false, cancelled_driver_id: null } } }
        // Hospital API response structure: { success: true, message: "...", data: { success: true, message: "..." } }
        if (response?.data) {
          // Extract nested data for fleet API if needed
          const responseData = isFleet && response.data?.data ? response.data.data : response.data;
          
          console.log('✅ [Manual Allocation] Allocation stopped successfully:', {
            allocation_stopped: responseData.allocation_stopped,
            driver_request_cancelled: responseData.driver_request_cancelled,
            cancelled_driver_id: responseData.cancelled_driver_id,
            message: responseData.message || response.message,
            isFleetUser: isFleet
          });
          
          // Handle real-time data from response
          if (responseData.allocation_stopped) {
            console.log('✅ [Manual Allocation] Allocation stopped - driver request cancelled:', responseData.driver_request_cancelled);
            if (responseData.cancelled_driver_id) {
              console.log('📋 [Manual Allocation] Cancelled driver ID:', responseData.cancelled_driver_id);
            }
            
            // For fleet users, re-trigger candidates API after successful stop allocation
            if (isFleet && onRefetchCandidates && bookingId) {
              console.log('🔄 [Manual Allocation] Fleet user - re-triggering candidates API after stop allocation');
              try {
                await onRefetchCandidates(bookingId);
                console.log('✅ [Manual Allocation] Candidates API re-triggered successfully');
              } catch (refetchError) {
                console.error('❌ [Manual Allocation] Error re-triggering candidates API:', refetchError);
                // Continue with closing even if refetch fails
              }
            }
          }
        }
      } catch (error) {
        console.error('❌ [Manual Allocation] Error calling stop-allocation API:', error);
        // Continue with closing even if API call fails
      }
    } else {
      console.warn('⚠️ [Manual Allocation] No bookingId available for stop-allocation API call');
    }
    
    // Stop allocation process (stop polling, reset states)
    if (onStopAllocation) {
      console.log('🛑 [Manual Allocation] Calling onStopAllocation to stop polling and reset states');
      onStopAllocation();
    }
    
    onClose();
  };

  // DEPRECATED: Simulation function removed
  // Confirmation modal only appears after real-time driver assignment via API
  const handleSimulateAccept = () => {
    console.warn('⚠️ [MANUAL ALLOCATION] handleSimulateAccept called - simulation disabled');
    console.warn('⚠️ [MANUAL ALLOCATION] Confirmation modal only appears after real-time API driver assignment');
    // Do nothing - confirmation is only shown via real-time API data
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Manual Allocation</h2>
            <p className="text-sm text-gray-600 mt-1">
              {showConfirmation 
                ? "Booking confirmed successfully!" 
                : allocationStatus?.driver_response?.status === 'ACCEPTED'
                ? "Driver accepted the assignment"
                : allocationStatus?.driver_response?.status === 'DECLINED'
                ? "Driver declined the assignment"
                : allocationStatus?.ui_state === 'TIMEOUT_PICK_ANOTHER'
                ? "Driver did not respond in time"
                : driverResponse === 'timeout' || driverResponse === 'rejected'
                ? "Driver not available"
                : isPollingAllocation
                ? "Polling allocation status..."
                : "Waiting for driver response..."
              }
            </p>
          </div>
          <button
            onClick={() => {
              // Once accepted, there's nothing left to cancel — closing the
              // modal must never call handleCancel here, since that would
              // hit the backend's stop-allocation call and (if it weren't
              // rejected server-side) erase a driver's real acceptance.
              const isAccepted = showConfirmation || allocationStatus?.driver_response?.status === 'ACCEPTED';
              if (isAccepted) {
                onClose();
              } else {
                handleCancel();
              }
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Ambulance Type Selection List - Show when user wants to select different ambulance type */}
          {showAmbulanceSelection && ambulances.length > 0 && (
            <div className="space-y-4">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Select an Ambulance Type</h3>
                <p className="text-sm text-gray-600">Choose an ambulance type from the available options:</p>
              </div>
              
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {ambulances.map((ambulance, index) => {
                  const isSelected = currentSelectedAmbulance?.type_id === ambulance.type_id || 
                                     (currentSelectedAmbulance?.type === ambulance.type && !currentSelectedAmbulance?.type_id);
                  
                  return (
                    <button
                      key={ambulance.type_id || index}
                      onClick={() => handleSelectAmbulanceType(ambulance)}
                      className={`w-full bg-white border-2 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 transition-all text-left ${
                        isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
                              <Truck className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                            </div>
                            <div className="flex-1">
                              <p className={`font-semibold ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                                {ambulance.name || ambulance.type}
                              </p>
                              {ambulance.description && (
                                <p className="text-sm text-gray-600">{ambulance.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isSelected ? 'bg-blue-600' : 'bg-gray-300'
                          }`}>
                            <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-gray-600'}`}>
                              {isSelected ? '✓' : '→'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setShowAmbulanceSelection(false)}
                className="w-full mt-4 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            </div>
          )}

          {/* Driver Selection List - Show when user wants to manually select */}
          {!showAmbulanceSelection && showDriverSelection && (driverResponse === 'rejected' || driverResponse === 'timeout') && (
            <div className="space-y-4">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Driver</h3>
                <p className="text-sm text-gray-600">Choose a driver from the available list:</p>
              </div>
              
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {driverPool.map((driver, index) => {
                  // Don't show the currently rejected driver
                  if (rejectedDriverIndex !== null && index === rejectedDriverIndex) {
                    return null;
                  }
                  
                  return (
                    <button
                      key={index}
                      onClick={() => handleSelectDriverManually(index)}
                      className="w-full bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <User className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{driver.name}</p>
                              <p className="text-sm text-gray-600">{driver.phone}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600 ml-14">
                            <span>⭐ {driver.rating}</span>
                            <span>📍 {driver.distance}</span>
                            <span>🚑 {driver.vehicleNumber}</span>
                            <span className="font-semibold text-blue-600">₹{driver.estimatedFare}</span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-semibold">→</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setShowDriverSelection(false)}
                className="w-full mt-4 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            </div>
          )}

          {/* Confirmation Modal */}
          {!showDriverSelection && !showAmbulanceSelection && showConfirmation && assignedDriver && (
            <div className="text-center">
              {/* Success Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>
              
              {/* Confirmation Title */}
              <h3 className="text-xl font-semibold text-green-900 mb-2">
                Booking Confirmed!
              </h3>
              <p className="text-green-700 mb-6">
                Your ambulance booking has been confirmed with the driver.
              </p>
              
              {/* Confirmed Details */}
              <div className="bg-green-50 rounded-lg p-4 mb-6 text-left">
                <h4 className="font-semibold text-gray-900 mb-3">Booking Details</h4>
                
                {/* Booking ID */}
                {(allocationStatus?.booking_id || bookingId) && (
                  <div className="mb-3 pb-3 border-b border-green-200">
                    <p className="text-sm text-gray-600">Booking ID:</p>
                    <p className="font-medium text-gray-900">
                      {allocationStatus?.booking_id || bookingId}
                    </p>
                  </div>
                )}
                
                {/* Ambulance Details */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Truck className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {assignedDriver?.ambulance?.name || 
                         allocationStatus?.vehicle?.type ||
                         selectedAmbulance?.name || 
                         'Ambulance'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {assignedDriver?.ambulance?.vehicleNumber || 
                         allocationStatus?.vehicle?.number ||
                         allocationStatus?.vehicle?.vehicle_number ||
                         selectedAmbulance?.plate_number || 
                         'N/A'} • ₹{assignedDriver?.ambulance?.price || 
                                   allocationStatus?.estimated_fare ||
                                   bookingData?.estimatedFare ||
                                   selectedAmbulance?.estimated_fare || 
                                   'N/A'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Driver Details */}
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <User className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {assignedDriver?.driver?.name || 
                         allocationStatus?.driver?.name ||
                         allocationStatus?.driver?.driver_name ||
                         selectedAmbulance?.driver?.name ||
                         'Driver Name'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {assignedDriver?.driver?.phone || 
                         allocationStatus?.driver?.phone ||
                         allocationStatus?.driver?.phone_number ||
                         selectedAmbulance?.driver?.phone || 
                         'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Status */}
                <div className="flex items-center justify-center gap-2 p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-800">Driver Confirmed</span>
                </div>
              </div>
              
              {/* Confirmation Button */}
              <button
                onClick={handleConfirmBooking}
                disabled={isConfirmingBooking}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isConfirmingBooking ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Confirming...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Confirm Booking
                  </>
                )}
              </button>
            </div>
          )}

          {/* Main Request Screen */}
          {!showDriverSelection && !showAmbulanceSelection && !showConfirmation && (
            <div className="space-y-6">
              {/* Allocation Status from API */}
              {allocationStatus && (
                <div className={`border rounded-lg p-4 ${
                  allocationStatus.ui_state === 'TIMEOUT_PICK_ANOTHER' 
                    ? 'bg-red-50 border-red-200'
                    : allocationStatus.driver_response?.status === 'ACCEPTED'
                    ? 'bg-green-50 border-green-200'
                    : allocationStatus.driver_response?.status === 'DECLINED'
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {isPollingAllocation && (
                      <Clock className="w-4 h-4 animate-spin text-blue-600" />
                    )}
                    <h4 className="font-semibold text-gray-900">Allocation Status</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Booking ID:</span>
                      <span className="font-medium">{allocationStatus.booking_id || bookingId || 'N/A'}</span>
                    </div>
                    {allocationStatus.hospital_name && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Hospital:</span>
                        <span className="font-medium">{allocationStatus.hospital_name}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Allocation Type:</span>
                      <span className="font-medium">MANUAL</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className={`font-medium ${
                        allocationStatus.ui_state === 'TIMEOUT_PICK_ANOTHER' 
                          ? 'text-red-600'
                          : allocationStatus.driver_response?.status === 'ACCEPTED'
                          ? 'text-green-600'
                          : allocationStatus.driver_response?.status === 'DECLINED'
                          ? 'text-orange-600'
                          : 'text-blue-600'
                      }`}>
                        {allocationStatus.ui_state || allocationStatus.driver_response?.status || 'Waiting...'}
                      </span>
                    </div>
                    {allocationStatus.driver_response && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Driver Response:</span>
                          <span className={`font-medium ${
                            allocationStatus.driver_response.status === 'ACCEPTED'
                              ? 'text-green-600'
                              : allocationStatus.driver_response.status === 'DECLINED'
                              ? 'text-red-600'
                              : 'text-gray-600'
                          }`}>
                            {allocationStatus.driver_response.status}
                          </span>
                        </div>
                        {countdown > 0 && !driverResponse && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Expires in:</span>
                            <span className="font-mono font-bold text-lg text-orange-600 bg-orange-50 px-3 py-1 rounded-md border-2 border-orange-300 animate-pulse">
                              {countdown}s
                            </span>
                          </div>
                        )}
                        {allocationStatus.driver_response.responded_at && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Responded at:</span>
                            <span className="font-medium text-xs">
                              {new Date(allocationStatus.driver_response.responded_at).toLocaleTimeString()}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Selected Ambulance Details - Use real data from allocationStatus */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Truck className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-900">
                      {allocationStatus?.vehicle?.type || 
                       currentSelectedAmbulance?.name || 
                       selectedAmbulance?.name || 
                       'Ambulance'}
                    </h3>
                    <p className="text-sm text-blue-700">
                      Vehicle: {allocationStatus?.vehicle?.number || 
                               allocationStatus?.vehicle?.vehicle_number ||
                               selectedAmbulance?.plate_number ||
                               'N/A'} • 
                      Estimated Fare: ₹{allocationStatus?.estimated_fare ? 
                                        Math.round(allocationStatus.estimated_fare) :
                                        (bookingData?.estimatedFare ? 
                                         Math.round(bookingData.estimatedFare) :
                                         (selectedAmbulance?.estimated_fare ? 
                                          Math.round(selectedAmbulance.estimated_fare) :
                                          'N/A'))}
                    </p>
                  </div>
                </div>

                {/* Driver Details - Use real data from allocationStatus */}
                <div className="border-t border-blue-200 pt-4">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">
                        {allocationStatus?.driver?.name || 
                         allocationStatus?.driver?.driver_name ||
                         selectedAmbulance?.driver?.name ||
                         'Driver Name'}
                      </p>
                      <p className="text-sm text-blue-700">Driver</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">
                        {allocationStatus?.driver?.phone || 
                         allocationStatus?.driver?.phone_number ||
                         selectedAmbulance?.driver?.phone ||
                         'N/A'}
                      </p>
                      <p className="text-sm text-blue-700">
                        {allocationStatus?.driver?.rating ? `⭐ ${allocationStatus.driver.rating}` : ''}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timer and Status - Use API allocation status if available */}
              {allocationStatus ? (
                <>
                  {/* Waiting for Response */}
                  {!allocationStatus.driver_response?.status && allocationStatus.ui_state !== 'TIMEOUT_PICK_ANOTHER' && (
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-3 mb-4">
                        <Clock className={`w-8 h-8 text-orange-600 ${isPollingAllocation ? 'animate-spin' : ''}`} />
                        {countdown > 0 && !driverResponse ? (
                          <span className="text-4xl font-mono font-bold text-orange-600 bg-orange-50 px-6 py-3 rounded-lg border-4 border-orange-400 shadow-lg animate-pulse">
                            {countdown}s
                          </span>
                        ) : (
                          <span className="text-2xl font-mono font-bold text-blue-600">Waiting...</span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-4">Waiting for driver to accept...</p>
                      
                      {/* Progress Bar */}
                      {countdown > 0 && !driverResponse && (
                        <div className="w-full bg-blue-200 rounded-full h-3 mb-4">
                          <div 
                            className="bg-blue-600 h-3 rounded-full transition-all duration-1000"
                            style={{ width: `${Math.min(100, Math.max(0, (countdown / (allocationTimeoutSec || 45)) * 100))}%` }}
                          ></div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Driver Accepted */}
                  {allocationStatus.driver_response?.status === 'ACCEPTED' && !showConfirmation && (
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
                        <CheckCircle className="w-8 h-8" />
                        <span className="text-xl font-semibold">Driver Accepted!</span>
                      </div>
                      <p className="text-gray-600">Preparing confirmation details...</p>
                    </div>
                  )}

                  {/* Driver Rejected or Timeout */}
                  {(allocationStatus.driver_response?.status === 'DECLINED' || allocationStatus.ui_state === 'TIMEOUT_PICK_ANOTHER') && (
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 text-orange-600 mb-4">
                        {allocationStatus.ui_state === 'TIMEOUT_PICK_ANOTHER' ? (
                          <Clock className="w-8 h-8" />
                        ) : (
                          <XCircle className="w-8 h-8" />
                        )}
                        <span className="text-xl font-semibold">
                          {allocationStatus.ui_state === 'TIMEOUT_PICK_ANOTHER' ? 'Driver Timeout' : 'Driver Declined'}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-6">Select a different ambulance to continue</p>
                      
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={async (e) => {
                            // Prevent any default behavior
                            e.preventDefault();
                            e.stopPropagation();
                            
                            console.log('🔄 [MANUAL ALLOCATION] Select Different Ambulance button clicked');
                            console.log('🔄 [MANUAL ALLOCATION] Current state:', {
                              ui_state: allocationStatus?.ui_state,
                              isTimeout: allocationStatus?.ui_state === 'TIMEOUT_PICK_ANOTHER',
                              isDeclined: allocationStatus?.driver_response?.status === 'DECLINED',
                              hasCallback: !!onRefetchCandidates,
                              bookingIdProp: bookingId,
                              bookingIdFromStatus: allocationStatus?.booking_id,
                              allocationStatus: allocationStatus
                            });
                            
                            // Get bookingId from prop or allocationStatus as fallback
                            // This bookingId comes from manual-allocation/start response
                            // It will be used in candidates API to exclude the assigned driver
                            const currentBookingId = bookingId || allocationStatus?.booking_id;
                            
                            console.log('🔄 [MANUAL ALLOCATION] Resolved bookingId:', currentBookingId);
                            console.log('🔄 [MANUAL ALLOCATION] This bookingId is from manual-allocation/start response');
                            console.log('🔄 [MANUAL ALLOCATION] It will be used to exclude assigned driver in candidates API');
                            
                            // Always trigger candidates API - prioritize callback, but ensure API is called
                            if (currentBookingId) {
                              if (onRefetchCandidates) {
                                console.log('✅ [MANUAL ALLOCATION] Using callback to refetch candidates');
                                console.log('✅ [MANUAL ALLOCATION] Calling onRefetchCandidates with bookingId:', currentBookingId);
                                try {
                                  // Call the API via callback
                                  console.log('🔄 [MANUAL ALLOCATION] Executing onRefetchCandidates callback...');
                                  await onRefetchCandidates(currentBookingId);
                                  console.log('✅ [MANUAL ALLOCATION] onRefetchCandidates callback completed');
                                  
                                  // Ensure modal is closed after API call
                                  console.log('✅ [MANUAL ALLOCATION] API call completed, closing modal');
                                  onClose();
                                } catch (error) {
                                  console.error('❌ [MANUAL ALLOCATION] Error calling onRefetchCandidates:', error);
                                  console.error('❌ [MANUAL ALLOCATION] Error details:', {
                                    message: error?.message,
                                    stack: error?.stack,
                                    response: error?.response?.data
                                  });
                                  // Close modal even on error
                                  onClose();
                                }
                              } else {
                                // Callback not available - this should not happen, but log it
                                console.error('❌ [MANUAL ALLOCATION] onRefetchCandidates callback is not available');
                                console.error('❌ [MANUAL ALLOCATION] This should not happen - callback should be passed');
                                // Still close modal
                                onClose();
                              }
                            } else {
                              console.error('❌ [MANUAL ALLOCATION] No bookingId available from prop or allocationStatus');
                              console.error('❌ [MANUAL ALLOCATION] Cannot call API without bookingId');
                              console.error('❌ [MANUAL ALLOCATION] Available data:', {
                                bookingIdProp: bookingId,
                                bookingIdFromStatus: allocationStatus?.booking_id,
                                hasAllocationStatus: !!allocationStatus
                              });
                              // Still close modal
                              onClose();
                            }
                          }}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                          Select Different Ambulance
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Fallback to simulated countdown if no API status */
                !driverResponse && (
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-3 mb-4">
                      <Clock className="w-8 h-8 text-orange-600" />
                      <span className="text-4xl font-mono font-bold text-orange-600 bg-orange-50 px-6 py-3 rounded-lg border-4 border-orange-400 shadow-lg animate-pulse">{countdown}s</span>
                    </div>
                    <p className="text-gray-600 mb-4">Waiting for driver to accept...</p>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-blue-200 rounded-full h-3 mb-4">
                      <div 
                        className="bg-blue-600 h-3 rounded-full transition-all duration-1000"
                        style={{ width: `${Math.min(100, Math.max(0, (countdown / (allocationTimeoutSec || 45)) * 100))}%` }}
                      ></div>
                    </div>

                    {/* Demo/Simulation button removed - confirmation only appears after real-time driver assignment via API */}
                  </div>
                )
              )}

              {/* Fallback UI for simulated acceptance removed - confirmation only appears after real-time API driver assignment */}

              {/* Driver Rejected or Timeout (fallback for simulated) */}
              {!allocationStatus && (driverResponse === 'rejected' || driverResponse === 'timeout') && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-orange-600 mb-4">
                    {driverResponse === 'timeout' ? (
                      <Clock className="w-8 h-8" />
                    ) : (
                      <XCircle className="w-8 h-8" />
                    )}
                    <span className="text-xl font-semibold">
                      {driverResponse === 'timeout' ? 'Driver Timeout' : 'Driver Rejected'}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-6">Select a different ambulance type to continue</p>
                  
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        // Close modal to return to ambulance type selection
                        console.log('🔄 [MANUAL ALLOCATION] Returning to ambulance type selection');
                        handleCancel();
                      }}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                    >
                      Select Different Ambulance Type
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {/* Hide Cancel as soon as the driver's acceptance is known — not just
            once showConfirmation flips — otherwise there's a brief window
            between "accepted" and the confirmation screen loading where
            Cancel is still live and could undo a real acceptance. */}
        {!showDriverSelection && !showAmbulanceSelection && !showConfirmation &&
          allocationStatus?.driver_response?.status !== 'ACCEPTED' && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualAllocationModal;